// Grid layout, road / walk graphs, collision spatial hash, road raster.
import * as THREE from 'three';
import type { AABB, CollisionResult, PathGraph, PathLane } from './types';

export const ROAD_W = 14;
export const HALF = ROAD_W / 2;
export const SIDEWALK = 3;
export const LANE = 3.5;
export const CURB_H = 0.1;
export const BLOCK = 56;
export const RIVER_PITCH = 90;
export const WATER_Y = -1.3;

export interface Region {
  key: string;
  c0: number;
  c1: number;
  r0: number;
  r1: number;
  /** which side the POI / entrance is on */
  front: 'N' | 'S' | 'E' | 'W';
  name: string;
}

export interface Block {
  c0: number;
  c1: number;
  r0: number;
  r1: number;
  landmark?: Region;
  /** outer rect (between road edges, includes sidewalk) */
  ox0: number;
  oz0: number;
  ox1: number;
  oz1: number;
  /** buildable rect (inside sidewalk) */
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  buda: boolean;
}

export interface Node {
  k: number;
  j: number;
  x: number;
  z: number;
  n: boolean;
  s: boolean;
  e: boolean;
  w: boolean;
}

export interface Segment {
  /** 'h' along x, 'v' along z */
  dir: 'h' | 'v';
  k: number;
  j: number;
  ax: number;
  az: number;
  bx: number;
  bz: number;
  bridge: boolean;
}

export const REGIONS: Region[] = [
  { key: 'apartment', c0: 8, c1: 8, r0: 4, r1: 4, front: 'S', name: 'Your Apartment' },
  { key: 'hospital', c0: 9, c1: 9, r0: 2, r1: 2, front: 'S', name: 'Central Hospital' },
  { key: 'police', c0: 8, c1: 8, r0: 7, r1: 7, front: 'S', name: 'Police HQ' },
  { key: 'parliament', c0: 6, c1: 6, r0: 3, r1: 4, front: 'E', name: 'Parliament' },
  { key: 'castle', c0: 4, c1: 4, r0: 5, r1: 6, front: 'E', name: 'Buda Castle' },
  { key: 'basilica', c0: 7, c1: 7, r0: 4, r1: 4, front: 'S', name: 'Basilica' },
  { key: 'market', c0: 6, c1: 6, r0: 7, r1: 7, front: 'E', name: 'Great Market Hall' },
  { key: 'station', c0: 10, c1: 10, r0: 4, r1: 4, front: 'W', name: 'Keleti Station' },
  { key: 'ruinbar', c0: 8, c1: 8, r0: 5, r1: 5, front: 'S', name: 'Ruin Bar District' },
  { key: 'bath', c0: 3, c1: 3, r0: 7, r1: 7, front: 'E', name: 'Thermal Bath' },
  { key: 'stadium', c0: 11, c1: 11, r0: 7, r1: 8, front: 'W', name: 'Pest Arena' },
  { key: 'docks', c0: 6, c1: 7, r0: 9, r1: 9, front: 'N', name: 'Danube Docks' },
  { key: 'garage', c0: 9, c1: 9, r0: 6, r1: 6, front: 'S', name: "Zoli's Garage" },
];

export class Layout {
  cols = 13; // block columns (index 5 = river)
  rows = 10;
  riverCol = 5;
  roadX: number[] = [];
  roadZ: number[] = [];
  riverCx = 0;
  bridgeRows = [2, 5, 8];
  blocks: Block[] = [];
  nodes: (Node | null)[] = [];
  segments: Segment[] = [];
  bounds: AABB;
  /** water band (x range) */
  waterX0 = 0;
  waterX1 = 0;
  /** promenade / bank geometry */
  bankX0 = 0; // buda bank wall outer x (water edge)
  bankX1 = 0;

  constructor() {
    // road x positions
    let x = 0;
    const xs: number[] = [0];
    for (let k = 0; k < this.cols; k++) {
      x += k === this.riverCol ? RIVER_PITCH : BLOCK + ROAD_W;
      xs.push(x);
    }
    const cx = (xs[0] + xs[xs.length - 1]) / 2;
    this.roadX = xs.map((v) => v - cx);
    const zs: number[] = [];
    for (let j = 0; j <= this.rows; j++) zs.push(j * (BLOCK + ROAD_W));
    const cz = (zs[0] + zs[zs.length - 1]) / 2;
    this.roadZ = zs.map((v) => v - cz);
    this.riverCx = (this.roadX[this.riverCol] + this.roadX[this.riverCol + 1]) / 2;
    // river: road edge -> 3 m promenade -> 1 m parapet -> water
    this.bankX0 = this.roadX[this.riverCol] + HALF + SIDEWALK + 1;
    this.bankX1 = this.roadX[this.riverCol + 1] - HALF - SIDEWALK - 1;
    this.waterX0 = this.bankX0;
    this.waterX1 = this.bankX1;
    const M = 18;
    this.bounds = { minX: this.roadX[0] - HALF - M, minZ: this.roadZ[0] - HALF - M, maxX: this.roadX[this.cols] + HALF + M, maxZ: this.roadZ[this.rows] + HALF + M, height: 0 };

    // blocks
    const taken = new Set<string>();
    for (const r of REGIONS) {
      for (let c = r.c0; c <= r.c1; c++) for (let j = r.r0; j <= r.r1; j++) taken.add(c + ',' + j);
      this.blocks.push(this.makeBlock(r.c0, r.c1, r.r0, r.r1, r));
    }
    for (let c = 0; c < this.cols; c++) {
      if (c === this.riverCol) continue;
      for (let j = 0; j < this.rows; j++) {
        if (taken.has(c + ',' + j)) continue;
        this.blocks.push(this.makeBlock(c, c, j, j));
      }
    }
    // segments + nodes
    const nk = this.cols + 1;
    const nj = this.rows + 1;
    this.nodes = new Array(nk * nj).fill(null);
    for (let k = 0; k <= this.cols; k++) {
      for (let j = 0; j < this.rows; j++) {
        if (!this.vSegExists(k, j)) continue;
        this.segments.push({ dir: 'v', k, j, ax: this.roadX[k], az: this.roadZ[j], bx: this.roadX[k], bz: this.roadZ[j + 1], bridge: false });
      }
    }
    for (let k = 0; k < this.cols; k++) {
      for (let j = 0; j <= this.rows; j++) {
        if (!this.hSegExists(k, j)) continue;
        this.segments.push({ dir: 'h', k, j, ax: this.roadX[k], az: this.roadZ[j], bx: this.roadX[k + 1], bz: this.roadZ[j], bridge: k === this.riverCol });
      }
    }
    for (let k = 0; k <= this.cols; k++) {
      for (let j = 0; j <= this.rows; j++) {
        const n = j > 0 && this.vSegExists(k, j - 1);
        const s = j < this.rows && this.vSegExists(k, j);
        const w = k > 0 && this.hSegExists(k - 1, j);
        const e = k < this.cols && this.hSegExists(k, j);
        if (!(n || s || w || e)) continue;
        this.nodes[k + j * nk] = { k, j, x: this.roadX[k], z: this.roadZ[j], n, s, e, w };
      }
    }
  }

  node(k: number, j: number): Node | null {
    if (k < 0 || j < 0 || k > this.cols || j > this.rows) return null;
    return this.nodes[k + j * (this.cols + 1)];
  }

  private makeBlock(c0: number, c1: number, r0: number, r1: number, landmark?: Region): Block {
    const ox0 = this.roadX[c0] + HALF;
    const ox1 = this.roadX[c1 + 1] - HALF;
    const oz0 = this.roadZ[r0] + HALF;
    const oz1 = this.roadZ[r1 + 1] - HALF;
    return { c0, c1, r0, r1, landmark, ox0, oz0, ox1, oz1, x0: ox0 + SIDEWALK, z0: oz0 + SIDEWALK, x1: ox1 - SIDEWALK, z1: oz1 - SIDEWALK, buda: c1 < this.riverCol };
  }

  private interiorV(k: number, j: number): boolean {
    for (const r of REGIONS) if (k > r.c0 && k <= r.c1 && j >= r.r0 && j <= r.r1) return true;
    return false;
  }
  private interiorH(k: number, j: number): boolean {
    for (const r of REGIONS) if (j > r.r0 && j <= r.r1 && k >= r.c0 && k <= r.c1) return true;
    return false;
  }
  vSegExists(k: number, j: number): boolean {
    if (k < 0 || k > this.cols || j < 0 || j >= this.rows) return false;
    return !this.interiorV(k, j);
  }
  hSegExists(k: number, j: number): boolean {
    if (k < 0 || k >= this.cols || j < 0 || j > this.rows) return false;
    if (k === this.riverCol && !this.bridgeRows.includes(j)) return false;
    return !this.interiorH(k, j);
  }
  blockAt(c: number, j: number): Block | null {
    for (const b of this.blocks) if (c >= b.c0 && c <= b.c1 && j >= b.r0 && j <= b.r1) return b;
    return null;
  }
  isRiverX(x: number): boolean {
    return x > this.waterX0 && x < this.waterX1;
  }
}

// ----------------------------------------------------------- graph builder ----

interface GNode {
  x: number;
  z: number;
  out: number[];
  in: number[];
}

export class GraphBuilder {
  nodes = new Map<string, GNode>();
  lanes: PathLane[] = [];
  private key(x: number, z: number): string {
    return (Math.round(x * 10) / 10).toFixed(1) + ',' + (Math.round(z * 10) / 10).toFixed(1);
  }
  private node(x: number, z: number): GNode {
    const k = this.key(x, z);
    let n = this.nodes.get(k);
    if (!n) {
      n = { x, z, out: [], in: [] };
      this.nodes.set(k, n);
    }
    return n;
  }
  hasNode(x: number, z: number): boolean {
    return this.nodes.has(this.key(x, z));
  }
  lane(ax: number, az: number, bx: number, bz: number, endsAtIntersection: boolean): PathLane {
    const id = this.lanes.length;
    const l: PathLane = { id, a: new THREE.Vector3(ax, 0, az), b: new THREE.Vector3(bx, 0, bz), next: [], length: Math.hypot(bx - ax, bz - az), endsAtIntersection };
    this.lanes.push(l);
    this.node(ax, az).out.push(id);
    this.node(bx, bz).in.push(id);
    return l;
  }
  /** both directions */
  pair(ax: number, az: number, bx: number, bz: number, ends = true): void {
    this.lane(ax, az, bx, bz, ends);
    this.lane(bx, bz, ax, az, ends);
  }
  build(cell: number): PathGraph {
    for (const l of this.lanes) {
      const nb = this.node(l.b.x, l.b.z);
      const na = this.key(l.a.x, l.a.z);
      for (const o of nb.out) {
        const ol = this.lanes[o];
        if (this.key(ol.b.x, ol.b.z) === na) continue; // no U-turn
        l.next.push(o);
      }
    }
    return new LaneGraph(this.lanes, cell);
  }
}

class LaneGraph implements PathGraph {
  private grid = new Map<number, number[]>();
  private cell: number;
  constructor(
    public lanes: PathLane[],
    cell: number,
  ) {
    this.cell = cell;
    for (const l of this.lanes) {
      const x0 = Math.min(l.a.x, l.b.x) - 1, x1 = Math.max(l.a.x, l.b.x) + 1;
      const z0 = Math.min(l.a.z, l.b.z) - 1, z1 = Math.max(l.a.z, l.b.z) + 1;
      for (let cx = Math.floor(x0 / cell); cx <= Math.floor(x1 / cell); cx++) {
        for (let cz = Math.floor(z0 / cell); cz <= Math.floor(z1 / cell); cz++) {
          const k = this.k(cx, cz);
          let arr = this.grid.get(k);
          if (!arr) {
            arr = [];
            this.grid.set(k, arr);
          }
          arr.push(l.id);
        }
      }
    }
  }
  private k(cx: number, cz: number): number {
    return (cx + 32768) * 65536 + (cz + 32768);
  }
  nearest(x: number, z: number): { id: number; t: number; dist: number } {
    const cx = Math.floor(x / this.cell), cz = Math.floor(z / this.cell);
    let best = -1, bt = 0, bd = Infinity;
    const test = (id: number) => {
      const l = this.lanes[id];
      const ax = l.a.x, az = l.a.z, dx = l.b.x - ax, dz = l.b.z - az;
      const len2 = dx * dx + dz * dz;
      let t = len2 > 0 ? ((x - ax) * dx + (z - az) * dz) / len2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const px = ax + dx * t - x, pz = az + dz * t - z;
      const d = px * px + pz * pz;
      if (d < bd) {
        bd = d;
        best = id;
        bt = t;
      }
    };
    for (let ring = 0; ring <= 6; ring++) {
      for (let ix = cx - ring; ix <= cx + ring; ix++) {
        for (let iz = cz - ring; iz <= cz + ring; iz++) {
          if (ring > 0 && Math.abs(ix - cx) !== ring && Math.abs(iz - cz) !== ring) continue;
          const arr = this.grid.get(this.k(ix, iz));
          if (arr) for (let i = 0; i < arr.length; i++) test(arr[i]);
        }
      }
      // stop once the best found is closer than the next ring's boundary
      if (best >= 0 && Math.sqrt(bd) < ring * this.cell) break;
    }
    if (best < 0) {
      for (let i = 0; i < this.lanes.length; i++) test(i);
    }
    return { id: best, t: bt, dist: Math.sqrt(bd) };
  }
}

// -------------------------------------------------------------- road graph ----

export function buildRoadGraph(L: Layout): PathGraph {
  const gb = new GraphBuilder();
  // road lanes between intersection squares
  for (const s of L.segments) {
    if (s.dir === 'h') {
      // eastbound (+x): right = +z
      gb.lane(s.ax + HALF, s.az + LANE, s.bx - HALF, s.bz + LANE, true);
      // westbound (-x): right = -z
      gb.lane(s.bx - HALF, s.bz - LANE, s.ax + HALF, s.az - LANE, true);
    } else {
      // southbound (+z): right = -x
      gb.lane(s.ax - LANE, s.az + HALF, s.bx - LANE, s.bz - HALF, true);
      // northbound (-z): right = +x
      gb.lane(s.bx + LANE, s.bz - HALF, s.ax + LANE, s.az + HALF, true);
    }
  }
  // connectors inside intersections: entry point (per incoming dir) -> exit point (per outgoing dir), no U-turn
  for (const n of L.nodes) {
    if (!n) continue;
    const X = n.x, Z = n.z;
    // incoming directions: from W (eastbound), from E (westbound), from N (southbound), from S (northbound)
    const entries: { from: 'w' | 'e' | 'n' | 's'; x: number; z: number }[] = [];
    if (n.w) entries.push({ from: 'w', x: X - HALF, z: Z + LANE });
    if (n.e) entries.push({ from: 'e', x: X + HALF, z: Z - LANE });
    if (n.n) entries.push({ from: 'n', x: X - LANE, z: Z - HALF });
    if (n.s) entries.push({ from: 's', x: X + LANE, z: Z + HALF });
    const exits: { to: 'w' | 'e' | 'n' | 's'; x: number; z: number }[] = [];
    if (n.e) exits.push({ to: 'e', x: X + HALF, z: Z + LANE });
    if (n.w) exits.push({ to: 'w', x: X - HALF, z: Z - LANE });
    if (n.s) exits.push({ to: 's', x: X - LANE, z: Z + HALF });
    if (n.n) exits.push({ to: 'n', x: X + LANE, z: Z - HALF });
    for (const en of entries) {
      for (const ex of exits) {
        if (en.from === ex.to) continue; // U-turn
        gb.lane(en.x, en.z, ex.x, ex.z, false);
      }
    }
  }
  return gb.build(20);
}

// -------------------------------------------------------------- walk graph ----

export function buildWalkGraph(L: Layout): PathGraph {
  const gb = new GraphBuilder();
  const IN = SIDEWALK / 2;
  // block loops
  for (const b of L.blocks) {
    const x0 = b.ox0 + IN, x1 = b.ox1 - IN, z0 = b.oz0 + IN, z1 = b.oz1 - IN;
    gb.pair(x0, z0, x1, z0);
    gb.pair(x1, z0, x1, z1);
    gb.pair(x1, z1, x0, z1);
    gb.pair(x0, z1, x0, z0);
  }
  // promenades along the river (both banks), nodes at every cross street +- (HALF+IN)
  const promX = [L.roadX[L.riverCol] + HALF + IN, L.roadX[L.riverCol + 1] - HALF - IN];
  for (const px of promX) {
    const zs: number[] = [];
    for (let j = 0; j <= L.rows; j++) {
      zs.push(L.roadZ[j] - HALF - IN);
      zs.push(L.roadZ[j] + HALF + IN);
    }
    zs.sort((a, c) => a - c);
    const uniq = zs.filter((v, i) => i === 0 || v - zs[i - 1] > 0.5);
    for (let i = 0; i + 1 < uniq.length; i++) gb.pair(px, uniq[i], px, uniq[i + 1]);
  }
  // crossings at intersections
  for (const n of L.nodes) {
    if (!n) continue;
    const X = n.x, Z = n.z;
    const c = HALF + IN;
    const NW = [X - c, Z - c], NE = [X + c, Z - c], SW = [X - c, Z + c], SE = [X + c, Z + c];
    const tryPair = (a: number[], b: number[]) => {
      if (gb.hasNode(a[0], a[1]) && gb.hasNode(b[0], b[1])) gb.pair(a[0], a[1], b[0], b[1]);
    };
    if (n.n) tryPair(NW, NE);
    if (n.s) tryPair(SW, SE);
    if (n.w) tryPair(NW, SW);
    if (n.e) tryPair(NE, SE);
  }
  // bridge walkways (both sidewalks)
  for (const j of L.bridgeRows) {
    const Z = L.roadZ[j];
    for (const dz of [-(HALF + IN), HALF + IN]) gb.pair(promX[0], Z + dz, promX[1], Z + dz);
  }
  return gb.build(16);
}

// ------------------------------------------------------------- collisions ----

export class ColliderHash {
  private cells: AABB[][] = [];
  private ncx: number;
  private ncz: number;
  private ox: number;
  private oz: number;
  readonly cell = 16;
  constructor(bounds: AABB, pad = 64) {
    this.ox = bounds.minX - pad;
    this.oz = bounds.minZ - pad;
    this.ncx = Math.ceil((bounds.maxX - bounds.minX + pad * 2) / this.cell);
    this.ncz = Math.ceil((bounds.maxZ - bounds.minZ + pad * 2) / this.cell);
    this.cells = new Array(this.ncx * this.ncz);
  }
  insert(b: AABB): void {
    const cx0 = Math.max(0, Math.floor((b.minX - this.ox) / this.cell));
    const cx1 = Math.min(this.ncx - 1, Math.floor((b.maxX - this.ox) / this.cell));
    const cz0 = Math.max(0, Math.floor((b.minZ - this.oz) / this.cell));
    const cz1 = Math.min(this.ncz - 1, Math.floor((b.maxZ - this.oz) / this.cell));
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const i = cx + cz * this.ncx;
        (this.cells[i] ??= []).push(b);
      }
    }
  }
  resolveCircle(x: number, z: number, r: number): CollisionResult | null {
    let nx = 0, nz = 0, depth = 0, any = false;
    const cell = this.cell;
    for (let iter = 0; iter < 3; iter++) {
      let moved = false;
      const cx0 = Math.max(0, Math.floor((x - r - this.ox) / cell));
      const cx1 = Math.min(this.ncx - 1, Math.floor((x + r - this.ox) / cell));
      const cz0 = Math.max(0, Math.floor((z - r - this.oz) / cell));
      const cz1 = Math.min(this.ncz - 1, Math.floor((z + r - this.oz) / cell));
      for (let cx = cx0; cx <= cx1; cx++) {
        for (let cz = cz0; cz <= cz1; cz++) {
          const arr = this.cells[cx + cz * this.ncx];
          if (!arr) continue;
          for (let i = 0; i < arr.length; i++) {
            const b = arr[i];
            if (x + r <= b.minX || x - r >= b.maxX || z + r <= b.minZ || z - r >= b.maxZ) continue;
            const px = x < b.minX ? b.minX : x > b.maxX ? b.maxX : x;
            const pz = z < b.minZ ? b.minZ : z > b.maxZ ? b.maxZ : z;
            let dx = x - px, dz = z - pz;
            const d2 = dx * dx + dz * dz;
            if (d2 >= r * r) continue;
            let push: number;
            if (d2 > 1e-9) {
              const d = Math.sqrt(d2);
              dx /= d;
              dz /= d;
              push = r - d;
            } else {
              // centre inside the box: exit through the nearest face
              const dl = x - b.minX, dr = b.maxX - x, dn = z - b.minZ, ds = b.maxZ - z;
              const m = Math.min(dl, dr, dn, ds);
              if (m === dl) { dx = -1; dz = 0; push = dl + r; }
              else if (m === dr) { dx = 1; dz = 0; push = dr + r; }
              else if (m === dn) { dx = 0; dz = -1; push = dn + r; }
              else { dx = 0; dz = 1; push = ds + r; }
            }
            x += dx * push;
            z += dz * push;
            nx += dx * push;
            nz += dz * push;
            depth += push;
            moved = true;
            any = true;
          }
        }
      }
      if (!moved) break;
    }
    if (!any) return null;
    const l = Math.hypot(nx, nz) || 1;
    return { x, z, nx: nx / l, nz: nz / l, depth };
  }
}

/** 1 m raster of road cells for isRoad */
export class RoadRaster {
  data: Uint8Array;
  w: number;
  h: number;
  ox: number;
  oz: number;
  constructor(bounds: AABB) {
    this.ox = Math.floor(bounds.minX);
    this.oz = Math.floor(bounds.minZ);
    this.w = Math.ceil(bounds.maxX - this.ox) + 1;
    this.h = Math.ceil(bounds.maxZ - this.oz) + 1;
    this.data = new Uint8Array(this.w * this.h);
  }
  fill(x0: number, z0: number, x1: number, z1: number): void {
    const ix0 = Math.max(0, Math.floor(x0 - this.ox)), ix1 = Math.min(this.w - 1, Math.ceil(x1 - this.ox) - 1);
    const iz0 = Math.max(0, Math.floor(z0 - this.oz)), iz1 = Math.min(this.h - 1, Math.ceil(z1 - this.oz) - 1);
    for (let z = iz0; z <= iz1; z++) for (let x = ix0; x <= ix1; x++) this.data[x + z * this.w] = 1;
  }
  get(x: number, z: number): boolean {
    const ix = Math.floor(x - this.ox), iz = Math.floor(z - this.oz);
    if (ix < 0 || iz < 0 || ix >= this.w || iz >= this.h) return false;
    return this.data[ix + iz * this.w] === 1;
  }
}
